import { useEffect } from "react";
import { useLang } from "../i18n/LanguageContext";
import { META, SITE_URL, BRAND, OG_IMAGE, LANGS, localize } from "../lib/seoData";

const LOCALE = { de: "de_CH", en: "en_US", fr: "fr_FR" };
const head = () => document.head;

const upsertMeta = (attr, key, content) => {
  let el = head().querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    head().appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let el = head().querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    head().appendChild(el);
  }
  el.setAttribute("href", href);
};

const clearMarked = (sel) => head().querySelectorAll(sel).forEach((n) => n.remove());

// Imperatively manages document <head> so static default tags (index.html) are
// UPDATED per route — no duplicate title/description/og tags (Bing-safe).
export const Seo = ({ pageKey, path, title, description, schema = [] }) => {
  const { lang } = useLang();
  const meta = META[pageKey];
  const m = meta ? meta[lang] : {};
  const finalTitle = title || m.title || BRAND;
  const finalDesc = description || m.description || "";
  const basePath = path || (meta ? meta.path : "/");
  const url = `${SITE_URL}${localize(basePath, lang)}`;
  const docTitle = pageKey === "home" ? finalTitle : `${finalTitle} · ${BRAND}`;
  const schemaKey = JSON.stringify(schema);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = docTitle;

    upsertMeta("name", "description", finalDesc);
    upsertCanonical(url);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:locale", LOCALE[lang]);
    upsertMeta("property", "og:title", docTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", docTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    // og:locale:alternate (rebuilt per language; clear ALL incl. static defaults)
    clearMarked('meta[property="og:locale:alternate"]');
    LANGS.filter((l) => l !== lang).forEach((l) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", LOCALE[l]);
      el.setAttribute("data-seo", "locale-alt");
      head().appendChild(el);
    });

    // hreflang alternates (rebuilt per route)
    clearMarked('link[data-seo="alt"]');
    [...LANGS, "x-default"].forEach((l) => {
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", l);
      el.setAttribute("href", `${SITE_URL}${localize(basePath, l === "x-default" ? "de" : l)}`);
      el.setAttribute("data-seo", "alt");
      head().appendChild(el);
    });

    // JSON-LD: BreadcrumbList (inner pages) + any passed schema (Product / BlogPosting)
    clearMarked('script[data-seo="ld"]');
    const list = [...schema];
    if (pageKey && pageKey !== "home" && meta) {
      list.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: BRAND, item: SITE_URL },
          { "@type": "ListItem", position: 2, name: finalTitle, item: url },
        ],
      });
    }
    list.forEach((s) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo", "ld");
      el.textContent = JSON.stringify(s);
      head().appendChild(el);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, docTitle, finalDesc, url, basePath, pageKey, schemaKey]);

  return null;
};

export default Seo;
